import { useEffect, useState } from "react";
import { Link } from "wouter";
import { ArrowLeft, Clock, Radio } from "lucide-react";
import { SiteLogo } from "@/components/SiteLogo";
import { SITE_NAME, SITE_TAGLINE } from "@shared/site";

interface Station {
  id: string;
  name: string;
  tagline: string;
  genre: string;
  genreColor: string;
  frequency: string;
  image: string;
  morningBlock: string;
  afternoonBlock: string;
  eveningBlock: string;
  latenightBlock: string;
}

const STATIONS: Station[] = [
  {
    id: "eclectic",
    name: "The Pickle Jar",
    tagline: "Eclectic Pickleball Hits",
    genre: "Eclectic",
    genreColor: "#7dc832",
    frequency: "PB 98.7",
    image: "https://d2xsxph8kpxj0f.cloudfront.net/310419663030553231/bcYnHo9Fr4CTHXVDZRKzhn/pb-station-eclectic-4mnAjFQBbinhXhxp8TH7aa.webp",
    morningBlock: "Wake-Up Mix",
    afternoonBlock: "Court Classics",
    eveningBlock: "Prime Eclectic",
    latenightBlock: "Late Night Shuffle",
  },
  {
    id: "classic-rock",
    name: "Rock the Court",
    tagline: "Classic Rock for Old-School Ballers",
    genre: "Classic Rock",
    genreColor: "#f97316",
    frequency: "PB 104.3",
    image: "https://d2xsxph8kpxj0f.cloudfront.net/310419663030553231/bcYnHo9Fr4CTHXVDZRKzhn/pb-station-classic-rock-6rCGCpmEenG99PeczLSDZC.webp",
    morningBlock: "Classic Warm-Up",
    afternoonBlock: "Midday Riffs",
    eveningBlock: "Rock the Night",
    latenightBlock: "After-Hours Anthems",
  },
  {
    id: "country",
    name: "Dink & Twang",
    tagline: "Country Roads to the Kitchen",
    genre: "Country",
    genreColor: "#f59e0b",
    frequency: "PB 91.5",
    image: "https://d2xsxph8kpxj0f.cloudfront.net/310419663030553231/bcYnHo9Fr4CTHXVDZRKzhn/pb-station-country-XwY8k9MW73v4R5tfdfcsNZ.webp",
    morningBlock: "Country Morning",
    afternoonBlock: "Tailgate Mix",
    eveningBlock: "Sunset Twang",
    latenightBlock: "Honky-Tonk Late",
  },
  {
    id: "hiphop",
    name: "Dink Don't Stop",
    tagline: "Hip-Hop & Urban Beats",
    genre: "Hip-Hop / R&B",
    genreColor: "#a855f7",
    frequency: "PB 107.1",
    image: "https://d2xsxph8kpxj0f.cloudfront.net/310419663030553231/bcYnHo9Fr4CTHXVDZRKzhn/pb-station-hiphop-dmxbC8G2uWBh9wqhsCKttv.webp",
    morningBlock: "Morning Energy",
    afternoonBlock: "Court Swagger",
    eveningBlock: "Prime Beats",
    latenightBlock: "Late Night Heat",
  },
  {
    id: "pop",
    name: "Pop & Dink",
    tagline: "Pop Hits & Indie Vibes",
    genre: "Pop / Indie",
    genreColor: "#ec4899",
    frequency: "PB 95.3",
    image: "https://d2xsxph8kpxj0f.cloudfront.net/310419663030553231/bcYnHo9Fr4CTHXVDZRKzhn/pb-station-pop-gwTWFATbNayo9r62r6rGJJ.webp",
    morningBlock: "Pop Morning",
    afternoonBlock: "Social Mix",
    eveningBlock: "Indie Prime",
    latenightBlock: "Chill Indie Night",
  },
  {
    id: "edm",
    name: "Smash & Drop",
    tagline: "Electronic & EDM Drops",
    genre: "Electronic / EDM",
    genreColor: "#00d4ff",
    frequency: "PB 102.9",
    image: "https://d2xsxph8kpxj0f.cloudfront.net/310419663030553231/bcYnHo9Fr4CTHXVDZRKzhn/pb-station-edm-2ZuzZS8zsJRPBgvqpSHMYS.webp",
    morningBlock: "AM Energy",
    afternoonBlock: "Drop Zone",
    eveningBlock: "Peak Hours",
    latenightBlock: "After Dark EDM",
  },
  {
    id: "christian",
    name: "Grace & Dink",
    tagline: "Inspirational Music for the Court",
    genre: "Christian",
    genreColor: "#f0c040",
    frequency: "PB 88.9",
    image: "https://d2xsxph8kpxj0f.cloudfront.net/310419663030553231/bcYnHo9Fr4CTHXVDZRKzhn/pb-station-christian-gixdPWLXRM9msEi9gLvYfU.webp",
    morningBlock: "Morning Worship",
    afternoonBlock: "Midday Praise",
    eveningBlock: "Evening Reflection",
    latenightBlock: "Night Grace",
  },
  {
    id: "wind-down",
    name: "Wind Down",
    tagline: "Meditation, Relaxation & Hypnotic Sounds",
    genre: "Meditation / Hypnotic",
    genreColor: "#38bdf8",
    frequency: "PB 87.5",
    image: "/station-wind-down.png",
    morningBlock: "Morning Calm",
    afternoonBlock: "Midday Reset",
    eveningBlock: "Evening Wind-Down",
    latenightBlock: "Deep Sleep Sounds",
  },
  {
    id: "podcast",
    name: "The Pickldiction Podcast",
    tagline: "One Word. One Episode. 24/7.",
    genre: "Podcast",
    genreColor: "#c084fc",
    frequency: "PB 90.1",
    image: "/stations/pickle-podcast.png",
    morningBlock: "Episode Loop",
    afternoonBlock: "Episode Loop",
    eveningBlock: "Episode Loop",
    latenightBlock: "Episode Loop",
  },
];

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const TIME_BLOCKS = [
  { key: "morningBlock" as const, label: "Morning", hours: "6am – Noon", icon: "☀️" },
  { key: "afternoonBlock" as const, label: "Afternoon", hours: "Noon – 6pm", icon: "🎾" },
  { key: "eveningBlock" as const, label: "Evening", hours: "6pm – 11pm", icon: "🌆" },
  { key: "latenightBlock" as const, label: "Late Night", hours: "11pm – 6am", icon: "🌙" },
];

function useLocalTimezone() {
  const [tz, setTz] = useState("");
  const [localTime, setLocalTime] = useState("");

  useEffect(() => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setTz(timezone);

    const update = () => {
      setLocalTime(
        new Intl.DateTimeFormat("en-US", {
          hour: "numeric",
          minute: "2-digit",
          timeZoneName: "short",
          timeZone: timezone,
        }).format(new Date())
      );
    };
    update();
    const id = setInterval(update, 60_000);
    return () => clearInterval(id);
  }, []);

  return { tz, localTime };
}

export default function Schedule() {
  const { tz, localTime } = useLocalTimezone();
  const [selectedDay, setSelectedDay] = useState<string>("all");

  useEffect(() => {
    const prev = document.title;
    document.title = `Schedule — ${SITE_NAME}`;
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border/50 bg-background/95 backdrop-blur-md sticky top-0 z-40">
        <div className="container flex items-center gap-3 h-14">
          <Link href="/">
            <button className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors text-sm font-medium">
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </Link>
          <span className="text-border/50">|</span>
          <Link href="/" className="text-foreground hover:opacity-90 transition-opacity flex items-center gap-2">
            <SiteLogo className="h-8" />
          </Link>
        </div>
      </header>

      <main className="container py-10 md:py-14 max-w-5xl">
        {/* Page title */}
        <div className="mb-8">
          <p
            className="text-xs uppercase tracking-widest mb-1"
            style={{ color: "var(--pb-green, #7dc832)" }}
          >
            {SITE_NAME}
          </p>
          <h1
            className="text-3xl md:text-5xl font-black uppercase mb-2"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Broadcast Schedule
          </h1>
          <p className="text-muted-foreground text-sm md:text-base">
            {SITE_TAGLINE} · 8 stations + podcast · All live · 24/7 non-stop
          </p>
        </div>

        {/* Timezone info */}
        <div className="flex items-center gap-2 mb-8 text-xs text-muted-foreground bg-white/5 rounded-lg px-4 py-2.5 w-fit border border-border/40">
          <Clock className="w-3.5 h-3.5 shrink-0" />
          <span>All times shown in your local timezone</span>
          {tz && (
            <span className="font-medium text-foreground/70">
              · {tz} · {localTime}
            </span>
          )}
        </div>

        {/* Day filter */}
        <div className="flex gap-2 mb-8 flex-wrap">
          <button
            onClick={() => setSelectedDay("all")}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
              selectedDay === "all"
                ? "text-black"
                : "text-muted-foreground hover:text-foreground bg-white/5 hover:bg-white/10"
            }`}
            style={
              selectedDay === "all"
                ? { background: "var(--pb-green, #7dc832)" }
                : {}
            }
          >
            All Week
          </button>
          {DAYS.map((day) => (
            <button
              key={day}
              onClick={() => setSelectedDay(day)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                selectedDay === day
                  ? "text-black"
                  : "text-muted-foreground hover:text-foreground bg-white/5 hover:bg-white/10"
              }`}
              style={
                selectedDay === day
                  ? { background: "var(--pb-green, #7dc832)" }
                  : {}
              }
            >
              {day}
            </button>
          ))}
        </div>

        {/* Schedule grid: stations as rows, time blocks as columns */}
        <div className="space-y-4">
          {/* Column headers */}
          <div className="hidden md:grid md:grid-cols-[200px_repeat(4,1fr)] gap-3 mb-2">
            <div />
            {TIME_BLOCKS.map((block) => (
              <div key={block.key} className="text-center">
                <p className="text-xs font-bold uppercase tracking-wider text-foreground/60">
                  {block.icon} {block.label}
                </p>
                <p className="text-xs text-muted-foreground">{block.hours}</p>
              </div>
            ))}
          </div>

          {STATIONS.map((station) => (
            <div
              key={station.id}
              className="rounded-xl border border-border/40 overflow-hidden bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
            >
              {/* Mobile: stacked layout */}
              <div className="md:hidden">
                <div className="flex items-center gap-3 p-3 border-b border-border/30">
                  <img
                    src={station.image}
                    alt={station.name}
                    className="w-10 h-10 rounded-lg object-cover shrink-0"
                  />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-muted-foreground">{station.frequency}</span>
                      <span
                        className="text-xs font-semibold px-1.5 py-0.5 rounded-full"
                        style={{ color: station.genreColor, background: `${station.genreColor}18` }}
                      >
                        {station.genre}
                      </span>
                    </div>
                    <h3
                      className="font-bold text-sm leading-tight mt-0.5"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                    >
                      {station.name}
                    </h3>
                  </div>
                  <div className="ml-auto flex items-center gap-1.5 shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                    <span className="text-xs text-green-400 font-semibold">LIVE</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 divide-x divide-y divide-border/30">
                  {TIME_BLOCKS.map((block) => (
                    <div key={block.key} className="p-3">
                      <p className="text-xs text-muted-foreground mb-0.5">
                        {block.icon} {block.label}
                      </p>
                      <p className="text-xs font-medium text-foreground/80">{station[block.key]}</p>
                      <p className="text-xs text-muted-foreground/60">{block.hours}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Desktop: grid layout */}
              <div className="hidden md:grid md:grid-cols-[200px_repeat(4,1fr)] gap-0 items-stretch">
                {/* Station info column */}
                <div className="flex items-center gap-3 p-4 border-r border-border/30">
                  <img
                    src={station.image}
                    alt={station.name}
                    className="w-10 h-10 rounded-lg object-cover shrink-0"
                  />
                  <div className="min-w-0">
                    <p className="text-xs font-mono text-muted-foreground">{station.frequency}</p>
                    <h3
                      className="font-bold text-sm leading-tight"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                    >
                      {station.name}
                    </h3>
                    <span
                      className="text-xs font-semibold"
                      style={{ color: station.genreColor }}
                    >
                      {station.genre}
                    </span>
                  </div>
                </div>

                {/* Time block columns */}
                {TIME_BLOCKS.map((block, i) => (
                  <div
                    key={block.key}
                    className={`p-4 flex flex-col justify-center ${i < TIME_BLOCKS.length - 1 ? "border-r border-border/30" : ""}`}
                  >
                    <p className="text-sm font-semibold text-foreground/90">{station[block.key]}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <Radio className="w-3 h-3 text-green-400 shrink-0" />
                      <span className="text-xs text-muted-foreground">24/7 AutoDJ</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* AutoDJ note */}
        <div className="mt-8 p-4 rounded-xl bg-white/[0.03] border border-border/30 text-center">
          <p className="text-sm text-muted-foreground">
            All stations run{" "}
            <span className="font-semibold text-foreground/70">24/7 AutoDJ</span> — no dead air,
            ever. Curated playlists rotate continuously throughout the day.
          </p>
          <p className="text-xs text-muted-foreground/60 mt-1">
            Live DJ sets and guest programming coming soon.
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/50 mt-16">
        <div className="container py-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2.5 text-foreground">
            <SiteLogo className="h-7" />
            <span className="text-sm text-muted-foreground">
              {SITE_NAME} — {SITE_TAGLINE}
            </span>
          </div>
          <div className="flex items-center gap-5 text-xs text-muted-foreground flex-wrap">
            <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
            <Link href="/schedule" className="hover:text-foreground transition-colors">Schedule</Link>
            <Link href="/advertise" className="hover:text-foreground transition-colors">Advertise</Link>
            <Link href="/contact" className="hover:text-foreground transition-colors">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
